import { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Link,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-core';
import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import { CodeRepoRow, useCodeRepoTabViewModel } from './CodeRepoTabViewModel';
import { CodeRepoModalView } from './CodeRepoModalView';
import { CodeRepoStatusBadge } from '../v2-common/CodeRepoStatusBadge';

export const CodeRepoTabView = memo(function CodeRepoTabView() {
  const vm = useCodeRepoTabViewModel();

  if (!vm) return null;

  return (
    <>
      <SpaceBetween size="m">
        <Container
          header={
            <Header
              variant="h3"
              actions={
                <Button
                  iconName="add-plus"
                  disabled={!vm.canAddRepo}
                  disabledReason="Only one repository can be added"
                  onClick={vm.openAddModal}
                >
                  Add repository
                </Button>
              }
              counter={vm.rows.length > 0 ? `(${vm.rows.length})` : undefined}
            >
              Code repository
            </Header>
          }
        >
          {vm.rows.length === 0 ? (
            <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'xxl' }}>
              No repositories added yet. Click &quot;Add repository&quot; to get started.
            </Box>
          ) : (
            <Table<CodeRepoRow>
              columnDefinitions={[
                {
                  id: 'label',
                  header: 'Title',
                  cell: (row) =>
                    row.url ? (
                      <Link href={row.url} external>
                        {row.label || row.url}
                      </Link>
                    ) : (
                      <Box>{row.label || '—'}</Box>
                    ),
                },
                { id: 'branch', header: 'Branch', cell: (row) => row.branch || '—' },
                { id: 'type', header: 'Type', cell: (row) => row.repoType },
                {
                  id: 'appsec',
                  header: 'Security review',
                  cell: (row) => <CodeRepoStatusBadge status={row.appsecStatus} />,
                },
                {
                  id: 'opensource',
                  header: 'Open source',
                  cell: (row) =>
                    vm.isExternalAudience ? (
                      <CodeRepoStatusBadge status={row.openSourceStatus} />
                    ) : (
                      '—'
                    ),
                },
                {
                  id: 'actions',
                  header: '',
                  width: 120,
                  minWidth: 120,
                  cell: (row) => (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        variant="icon"
                        iconName="edit"
                        ariaLabel="Edit repository"
                        onClick={() => vm.openEditModal(row)}
                      />
                      <Button
                        variant="icon"
                        iconName="remove"
                        ariaLabel="Delete repository"
                        onClick={() => vm.requestDelete(row)}
                      />
                    </SpaceBetween>
                  ),
                },
              ]}
              items={vm.rows as CodeRepoRow[]}
              trackBy="index"
              variant="embedded"
            />
          )}
        </Container>
        <Alert type="warning">
          Please grant the required access permissions for your code repository.
        </Alert>
      </SpaceBetween>

      <CodeRepoModalView vm={vm} />

      {vm.deleteConfirmation.kind === 'confirming' && (
        <ConfirmationModalView
          header="Delete repository"
          body={`Are you sure you want to remove "${vm.deleteConfirmation.row.label || vm.deleteConfirmation.row.url}"?`}
          confirmLabel="Delete"
          isConfirming={false}
          onDismiss={vm.cancelDelete}
          onConfirm={vm.confirmDelete}
        />
      )}
    </>
  );
});
